import React, { useState, useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';
import {
  EVENEMENT_OUVRIR,
  enregistrerConsentement,
  lireConsentement,
} from '../lib/consentement';

/**
 * Bandeau de consentement.
 *
 * Refait le 27/08/2026 : il annonçait des cookies Google Analytics jamais
 * configurés (l'identifiant était resté « G-XXXXX »), du « contenu
 * personnalisé » qui n'existe pas, et surtout AUCUN de ses trois boutons
 * n'avait le moindre effet — la valeur écrite dans localStorage n'était relue
 * par personne. Le bouton « Tout accepter » était même inerte par construction
 * (il lisait `preferences` d'avant sa propre mise à jour).
 *
 * Il ne reste donc que ce que le site fait vraiment : les cookies nécessaires
 * et une mesure d'audience anonyme, refusable et réellement arrêtée en cas de
 * refus (voir src/lib/consentement.js).
 */
export default function CookieConsent() {
  const { t } = useLang();
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // toujours actif
    analytics: true, // mesure anonyme : active tant qu'elle n'est pas refusée
  });

  useEffect(() => {
    const enregistre = lireConsentement();
    if (!enregistre) {
      requestAnimationFrame(() => setShowBanner(true));
    } else {
      requestAnimationFrame(() =>
        setPreferences({ necessary: true, analytics: enregistre.analytics !== false })
      );
    }
  }, []);

  // « Gérer mes cookies » (pied de page) rouvre le bandeau : sans cela, un
  // choix fait une fois était définitif, ce que la loi n'admet pas.
  useEffect(() => {
    const ouvrir = () => setShowBanner(true);
    window.addEventListener(EVENEMENT_OUVRIR, ouvrir);
    return () => window.removeEventListener(EVENEMENT_OUVRIR, ouvrir);
  }, []);

  const appliquer = (choix) => {
    enregistrerConsentement(choix);
    setPreferences(choix);
    setShowBanner(false);
  };

  const handleAcceptAll = () => appliquer({ necessary: true, analytics: true });
  const handleRejectAll = () => appliquer({ necessary: true, analytics: false });
  const handleSavePreferences = () => appliquer({ ...preferences, necessary: true });

  const handleToggle = (key) => {
    if (key === 'necessary') return; // Ne pas permettre de désactiver les cookies nécessaires
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-br from-[#33403B] to-[#22403A] border-t-4 border-[#4AA294] text-[#F7F4EC] z-[9999] animate-[slideUp_0.4s_ease-out] shadow-[0_-4px_20px_rgba(0,0,0,0.3)]">
      <div className="max-w-[1400px] mx-auto px-10 py-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="flex flex-col gap-4">
          <h3 className="m-0 text-lg font-bold text-[#EDB82F] font-['Fredoka',_'Poppins',_sans-serif]">
            {t('cookies.bandeau.titre')}
          </h3>

          <p className="m-0 text-[13px] leading-relaxed text-[#E8DDD2] opacity-95">
            {t('cookies.bandeau.texte')}
          </p>

          {/* Préférences */}
          <div className="flex flex-col gap-3 my-2 p-3 bg-black/20 rounded-lg">
            <label className="flex items-start gap-3 cursor-pointer transition-opacity duration-300 hover:opacity-90">
              <input
                type="checkbox"
                checked={preferences.necessary}
                disabled
                readOnly
                className="mt-0.5 cursor-not-allowed w-[18px] h-[18px] accent-[#EDB82F] opacity-60"
              />
              <span className="flex flex-col gap-1 text-xs">
                <strong className="text-[#4AA294] font-semibold">{t('cookies.categories.necessaires')}</strong>
                <em className="not-italic text-[#B0B0B0] text-[11px]">{t('cookies.categories.necessairesDesc')}</em>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer transition-opacity duration-300 hover:opacity-90">
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={() => handleToggle('analytics')}
                className="mt-0.5 cursor-pointer w-[18px] h-[18px] accent-[#EDB82F]"
              />
              <span className="flex flex-col gap-1 text-xs">
                <strong className="text-[#4AA294] font-semibold">{t('cookies.categories.analytics')}</strong>
                <em className="not-italic text-[#B0B0B0] text-[11px]">{t('cookies.categories.analyticsDesc')}</em>
              </span>
            </label>

            {/* La case « Marketing » a été retirée : le site n'a ni régie
                publicitaire, ni profilage, ni reciblage. Une case à cocher qui
                ne commande rien donne l'impression inverse. */}
          </div>

          {/* Info Légale */}
          <p className="m-0 text-[11px] text-[#B0B0B0]">
            {t('cookies.bandeau.voirNotre')}{' '}
            <a href="/privacy" className="text-[#4AA294] underline transition-colors duration-300 hover:text-[#EDB82F]">{t('cookies.bandeau.politique')}</a>{' '}
            {t('cookies.bandeau.plusInfos')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 flex-wrap justify-end lg:justify-start w-full lg:w-auto">
          <button
            className="px-5 py-2.5 border-none rounded-md text-xs font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 font-inherit whitespace-nowrap bg-transparent border-2 border-[#4AA294] text-[#4AA294] hover:bg-[#4AA294]/10 hover:border-[#EDB82F] hover:text-[#EDB82F] flex-1 lg:flex-none min-w-[120px]"
            onClick={handleRejectAll}
          >
            {t('cookies.actions.refuser')}
          </button>

          <button
            className="px-5 py-2.5 border-none rounded-md text-xs font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 font-inherit whitespace-nowrap bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)] flex-1 lg:flex-none min-w-[120px]"
            onClick={handleSavePreferences}
          >
            {t('cookies.actions.personnaliser')}
          </button>

          <button
            className="px-5 py-2.5 border-none rounded-md text-xs font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 font-inherit whitespace-nowrap bg-gradient-to-br from-[#EDB82F] to-[#4AA294] text-white hover:-translate-y-0.5 hover:shadow-[0_6px_15px_rgba(212,165,116,0.4)] flex-1 lg:flex-none min-w-[120px]"
            onClick={handleAcceptAll}
          >
            {t('cookies.actions.accepter')}
          </button>
        </div>
      </div>

      {/* Keyframes for slideUp animation */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
